// src/components/templates/EventTemplate.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X,
  User,
  Globe,
  Search,
  Shield,
  Send,
  ChevronRight,
  MessageSquare,
  Bell,
  Flag,
  ThumbsUp,
  Calendar, 
  MapPin, 
  Users, 
  Gift, 
  Eye,
  Settings,  
  LogIn,
  Camera, 
  CheckCircle,
  CreditCard,
  Clock,
  Star,
  Share2,
  Heart,
  ExternalLink,
  Phone,
  Mail,
  EyeOff,
  Chrome,
  Lock,
  User as UserIcon,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { eventsService } from '../../services/eventsService';
import { BookingModal } from '../events/BookingModal';
import { GiftRegistryView } from '../events/GiftRegistryView';
import { EventGalleryView } from '../events/EventGalleryView';
import { MilestoneProgress } from '../events/MilestoneProgress';
import { messagesService } from '../../services/messagesService';
import { ShareModal } from '../books/modals/ShareModal';
import { EventManager } from '../events/EventManager';
import { firestoreService } from '../../services/firestoreService';
import { SharedHeader } from '../shared/SharedHeader';


export const EventTemplate = ({ bookData, bookId, user, customStyling = {} }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [updateText, setUpdateText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingUpdate, setSendingUpdate] = useState(false);
  const [showShareModal, setShowShareModal]=useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [viewMode, setViewMode] = useState('public'); 

  const handleViewAllMessages = (section) => {
    if (section === 'organizers') {
      setActiveSection('organizers');
    } else {
      setActiveSection('messages');
    }
  };

  const handleViewModeToggle = () => {
    if (viewMode === 'public') {
      // Check if user is logged in
      if (!user) {
        // Capture current URL to return after login
        const currentUrl = window.location.pathname + window.location.search;
        const returnUrl = encodeURIComponent(currentUrl);
        
        // Redirect to login with return URL
        window.location.href = `/login?returnTo=${returnUrl}`;
        return;
      }
      
      // Check if user is authorized
      if (!canManageEvent()) {
        alert('You are not authorized to manage this event. Only the event creator and organizers can access management features.');
        return;
      }
      
      setViewMode('management');
    } else {
      setViewMode('public');
    }
  };

  // Update canManageEvent to be more robust:
  const canManageEvent = () => {
    if (!user) return false;
    
    const organizers = bookData.organizers || [];
    
    // Check if user is the creator
    if (user.uid === bookData?.ownerId) return true;
    
    // ✅ FIXED: Check if user is in organizers list by comparing id property
    const isOrganizer = organizers.some(org => 
      org.id === user.uid || 
      (user.email && org.id === user.email) ||
      (user.email && org.email === user.email)
    );
    
    if (isOrganizer) return true;
    
    return false;
  };


  useEffect(() => {
    loadEventData();
  }, [bookId]);

  const loadEventData = async () => {
    try {
      if (bookId) {
        const [analyticsData, messagesData, updatesData] = await Promise.all([
          eventsService.getEventAnalytics(bookId),
          eventsService.getEventMessages(bookId),
          eventsService.getEventUpdates(bookId)
        ]);
        
        setAnalytics(analyticsData);
        setMessages(messagesData);
        setUpdates(updatesData);
      }
    } catch (error) {
      console.error('Error loading event data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update the sendMessage function
  const sendMessage = async () => {
    if (!messageText.trim() || sendingMessage) return;

    setSendingMessage(true);
    try {
      await eventsService.sendEventMessage(bookId, {
        content: messageText,
        needsModeration: bookData.eventSettings?.moderateMessages,
        authorName: user.displayName, 
        authorId: user.uid, 
        isOrganizer: false 
      });
      
      setMessageText('');
      await loadEventData(); // Reload messages
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Update the sendUpdate function
  const sendUpdate = async () => {
    if (!updateText.trim() || sendingUpdate) return;

    setSendingUpdate(true);
    try {
      await eventsService.sendEventUpdate(bookId, {
        content: updateText,
        needsModeration: bookData.eventSettings?.moderateUpdates,
        authorName: user.displayName, // Replace with actual organizer name
        authorId: user.uid, // Replace with actual organizer ID
        notifyAttendees: true
      });
      
      setUpdateText('');
      await loadEventData(); // Reload updates
    } catch (error) {
      console.error('Error sending update:', error);
    } finally {
      setSendingUpdate(false);
    }
  };

  const handleShare = () => {
      setShowShareModal(true);
      
      // Track share button click
      firestoreService.trackInteraction(bookId, {
        type: 'share_click',
        userId: user?.uid
      });
    };
    

  // Handle loading state
  if (!bookData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Safely access event settings
  const eventSettings = bookData.eventSettings || {};
  const contactInfo = bookData.contactInfo || {};
  const organizers = bookData.organizers || [];
  const primaryOrganizer = organizers.find(org => org.isMain) || organizers[0];
  
  // Check if event has passed
  const isEventPassed = eventSettings.eventDate && new Date(eventSettings.eventDate) < new Date();
  
  // Calculate time until event
  const eventDate = eventSettings.eventDate ? new Date(eventSettings.eventDate) : null;
  const timeUntilEvent = eventDate ? eventDate.getTime() - new Date().getTime() : 0;

  const sections = [
    { id: 'overview', label: 'Overview', icon: Calendar, always: true },
    { id: 'details', label: 'Details', icon: MapPin, show: true },
    { id: 'booking', label: 'Book Now', icon: CreditCard, show: eventSettings.publicBooking && eventSettings.requireBooking && !isEventPassed },
    { id: 'milestones', label: 'Progress', icon: CheckCircle, show: eventSettings.publicMilestones && eventSettings.milestoneTracking },
    { id: 'organizers', label: 'Organizers', icon: Users },
    { id: 'messages', label: 'Messages', icon: MessageSquare, show: eventSettings.enableMessages},
    { id: 'updates', label: 'Updates', icon: Bell, show: eventSettings.enableUpdates  },
    { id: 'gallery', label: 'Gallery', icon: Camera, show: eventSettings.publicGallery && eventSettings.galleryManagement },
    { id: 'gifts', label: 'Gift Registry', icon: Gift, show: eventSettings.giftRegistryPublic && eventSettings.giftRegistryEnabled }
  ].filter(section => section.always || section.show);

  const renderViewToggle = () => {
    return (
      <div className="fixed bottom-6 right-6 z-40">
        <div className="bg-white rounded-full shadow-lg border border-gray-200 overflow-hidden">
          <button
            onClick={handleViewModeToggle}
            className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors group"
          >
            {viewMode === 'public' ? (
              <>
                <Settings className="h-5 w-5 text-gray-600 group-hover:text-emerald-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-emerald-700">
                  Organiser View
                </span>
              </>
            ) : (
              <>
                <Eye className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">
                  Public View
                </span>
              </>
            )}
          </button>
        </div>

        {/* View Mode Indicator */}
        {viewMode === 'management' && (
          <div className="absolute -top-12 right-0 bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-medium">
            Management Mode
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" style={customStyling}>
      {viewMode === 'public' ? (
        <>
      {/* Hero Section */}
      <div 
        className="relative text-white overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${
            bookData?.styling?.primaryColor || bookData?.primaryColor || '#0795ff'
          }, ${
            bookData?.styling?.accentColor || bookData?.accentColor || '#8b5cf6'
          })`
        }}
      >
        {(bookData.heroImage || bookData.coverImageUrl) && (
          <div className="absolute inset-0">
            <img 
              src={bookData.heroImage || bookData.coverImageUrl} 
              alt={bookData.botName || 'Event'}
              className="w-full h-full object-cover opacity-30"
            />
          </div>
        )}
  
        <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-12">
          {/* Header */}
              <SharedHeader
                templateType="events"
                bookData={bookData}
                styling={customStyling}
                isOwner={isOwner}
                navigate={navigate}
              />
          <div className="text-center">
            {bookData.logoUrl && (
              <div className="flex justify-center mb-8">
                <img 
                  src={bookData.logoUrl} 
                  alt="Event logo" 
                  className="h-auto w-48 rounded-2xl shadow-lg"
                />
              </div>
            )}
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {bookData.botName || 'Event Name'}
            </h1>
            <p className="text-xl lg:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
              {bookData.description || 'Join us for an amazing event!'}
            </p>

            {/* Event Info Cards */}
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              {eventDate && (
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-6 py-4 flex items-center gap-3">
                  <Calendar className="h-6 w-6" />
                  <div className="text-left">
                    <p className="font-medium">{eventDate.toLocaleDateString()}</p>
                    <p className="text-sm opacity-75">{eventDate.toLocaleTimeString()}</p>
                  </div>
                </div>
              )}

              {eventSettings.eventLocation && (
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-6 py-4 flex items-center gap-3">
                  <MapPin className="h-6 w-6" />
                  <div className="text-left">
                    <p className="font-medium">{eventSettings.eventLocation}</p>
                    {eventSettings.venue && (
                      <p className="text-sm opacity-75">{eventSettings.venue}</p>
                    )}
                  </div>
                </div>
              )}

              {analytics && (
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-6 py-4 flex items-center gap-3">
                  <Users className="h-6 w-6" />
                  <div className="text-left">
                    <p className="font-medium">{analytics.confirmedBookings || 0} attending</p>
                    <p className="text-sm opacity-75">
                      {eventSettings.maxAttendees ? 
                        `${eventSettings.maxAttendees - (analytics.confirmedBookings || 0)} spots left` : 
                        'Unlimited capacity'
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Countdown Timer */}
            {!isEventPassed && timeUntilEvent > 0 && (
              <div className="mb-8">
                <CountdownTimer targetDate={eventDate} />
              </div>
            )}

            {/* Primary CTA - conditional */}
            {!isEventPassed && (
              <div className="flex flex-wrap justify-center gap-4">
                {/* Only show booking button if publicBooking is enabled */}
                {eventSettings.publicBooking && eventSettings.requireBooking && (
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 shadow-lg"
                  >
                    <CreditCard className="h-5 w-5" />
                    {eventSettings.requirePayment ? 
                      `Book Now - $${eventSettings.eventPrice || 0}` : 
                      'Reserve Your Spot - Free'
                    }
                  </button>
                )}
                
                <button 
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-emerald-600 transition-colors flex items-center gap-2"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="h-5 w-5" />
                  Share Event
                </button>
              </div>
            )}
            {isEventPassed && (
              <div className="bg-gray-800 bg-opacity-50 rounded-lg px-8 py-4 inline-block">
                <p className="text-lg font-medium">This event has concluded</p>
                <p className="text-sm opacity-75">Thank you to all who attended!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap font-medium transition-colors ${
                    activeSection === section.id
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-2 lg:px-4 py-8">
        {activeSection === 'overview' && (
          <EventOverview 
            bookData={bookData} 
            eventSettings={eventSettings}
            contactInfo={contactInfo}
            organizers={organizers}
            analytics={analytics}
            messages={messages}
            onViewAllMessages={handleViewAllMessages}
          />
        )}
        {activeSection === 'details' && (
          <EventDetails 
            bookData={bookData} 
            eventSettings={eventSettings} 
          />
        )}
        {activeSection === 'booking' && (
          <EventBookingSection 
            bookData={bookData} 
            eventSettings={eventSettings}
            onOpenBooking={() => setShowBookingModal(true)} 
          />
        )}
        {activeSection === 'milestones' && bookId && (
          <MilestoneProgress bookId={bookId} />
        )}
        {activeSection === 'organizers' && (
          <OrganizersSection organizers={organizers} />
        )}

        {activeSection === 'messages' && eventSettings.enableMessages && (
          <MessagesSection 
            messages={messages}
            bookId={bookId}
            eventSettings={eventSettings}
            messageText={messageText}
            setMessageText={setMessageText}
            sendMessage={sendMessage}
            sendingMessage={sendingMessage}
            onReload={loadEventData}
          />
        )}

        {activeSection === 'updates' && eventSettings.enableUpdates && (
          <UpdatesSection 
            updates={updates}
            bookId={bookId}
            eventSettings={eventSettings}
            updateText={updateText}
            setUpdateText={setUpdateText}
            sendUpdate={sendUpdate}
            sendingUpdate={sendingUpdate}
            onReload={loadEventData}
          />
        )}

        {activeSection === 'gallery' && bookId && (
          <EventGalleryView bookId={bookId} />
        )}
        {activeSection === 'gifts' && bookId && (
          <GiftRegistryView bookId={bookId} />
        )}
      </div>
        </>
      ) : (
        <EventManager
          bookId={bookId}
          book={bookData}
          user={user}
          eventSettings={bookData?.eventSettings || {}}
          isOpen={true}
          onClose={() => setViewMode('public')}
          onUpdate={async () => {
            // Reload event data
            await loadEventData();
          }}
        />
      )}

      {/* View Toggle Button - only show if user can manage */}
      {user && canManageEvent() && renderViewToggle()}

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          book={bookData}
          bookId={bookId}
          eventSettings={eventSettings}
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          onSuccess={() => {
            setShowBookingModal(false);
            loadEventData();
          }}
        />
      )}

      <ShareModal 
        content={bookData}
        contentType="event" 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)}
      />
    </div>
  );
};

const OrganizersSection = ({ organizers }) => (
  <div className="max-w-4xl mx-auto">
    <div className="bg-white rounded-2xl shadow-sm p-8">
      <h2 className="text-3xl font-bold mb-8 text-center">Meet the Organizers</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {organizers.map((organizer, index) => (
          <div key={organizer.id} className="text-center group">
            <div className="relative mb-6">
              <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg group-hover:scale-105 transition-transform">
                {organizer.avatar ? (
                  <img 
                    src={organizer.avatar} 
                    alt={organizer.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>
              
              {organizer.isMain && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700 border border-emerald-200">
                    <Star className="h-3 w-3 mr-1" />
                    Primary
                  </span>
                </div>
              )}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {organizer.name}
            </h3>
            
            <p className="text-emerald-600 font-medium mb-4">
              {organizer.role}
            </p>

            <div className="space-y-2">
              {organizer.email && (
                <a
                  href={`mailto:${organizer.email}`}
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">{organizer.email}</span>
                </a>
              )}
              
              {organizer.phone && (
                <a
                  href={`tel:${organizer.phone}`}
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors block"
                >
                  <Phone className="h-4 w-4" />
                  <span className="text-sm">{organizer.phone}</span>
                </a>
              )}
              
              {organizer.website && (
                <a
                  href={organizer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors block"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm">Website</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Messages Section Component
const MessagesSection = ({ 
  messages, 
  bookId, 
  eventSettings, 
  messageText, 
  setMessageText, 
  sendMessage, 
  sendingMessage,
  onReload 
}) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showComposer, setShowComposer] = useState(false); // ADD THIS STATE

  // Filter to show only public messages
  const filteredMessages = messages.filter(message => {
    if (message.isPublic !== true) return false;
    
    const matchesFilter = filter === 'all' || message.status === filter;
    const matchesSearch = message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.authorName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const canSendMessage = eventSettings.messagingRoles?.includes('anyone') || 
                        eventSettings.messagingRoles?.includes('booked_attendee');

  // Enhanced send message function to close form
  const handleSendMessage = async () => {
    await sendMessage();
    setShowComposer(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* REPLACE THIS ENTIRE HEADER SECTION */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {/* Title Row with Toggle Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Event Messages</h2>
            <p className="text-gray-600 mt-2">
              Connect with other attendees and organizers
            </p>
          </div>
          
          {/* Toggle Composer Button */}
          {canSendMessage && (
            <button
              onClick={() => setShowComposer(!showComposer)}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center gap-2 shadow-sm"
            >
              {showComposer ? (
                <>
                  <X className="h-5 w-5" />
                  Cancel
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Write Message
                </>
              )}
            </button>
          )}
        </div>

        {/* Search and Filter Row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-6 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">View All</option>
            <option value="approved">Approved</option>
            {eventSettings.moderateMessages && (
              <option value="pending">Pending</option>
            )}
          </select>
        </div>

        {/* Message Composer - CONDITIONALLY RENDERED */}
        {canSendMessage && showComposer && (
          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-6 border border-blue-200 animate-in slide-in-from-top duration-300">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 space-y-4">
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Share your thoughts, questions, or excitement about this event..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  {eventSettings.moderateMessages && (
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-orange-500" />
                      Messages are reviewed before appearing publicly
                    </p>
                  )}
                  <div className="flex items-center gap-3 ml-auto">
                    <button
                      onClick={() => {
                        setShowComposer(false);
                        setMessageText('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={!messageText.trim() || sendingMessage}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {sendingMessage ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {sendingMessage ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* END OF HEADER SECTION REPLACEMENT */}

      {/* Messages List - Keep this as is */}
      <div className="space-y-4">
        {filteredMessages.length > 0 ? (
          filteredMessages.map((message) => (
            <MessageCard 
              key={message.id} 
              bookId={bookId}
              message={message}
              eventSettings={eventSettings}
              onReload={onReload}
            />
          ))
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No messages yet</h3>
            <p className="text-gray-600">
              {canSendMessage ? 
                'Be the first to start the conversation!' : 
                'Messages will appear here when attendees start sharing.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// NEW: Updates Section Component
const UpdatesSection = ({ 
  updates, 
  bookId, 
  eventSettings, 
  updateText, 
  setUpdateText, 
  sendUpdate, 
  sendingUpdate,
  onReload 
}) => {
  const [showComposer, setShowComposer] = useState(false); // NEW STATE
  const canPostUpdate = eventSettings.updatesRoles?.includes('organizer');
  
  // Filter to show only public updates
  const publicUpdates = updates.filter(update => update.isPublic === true);

  // Enhanced send update function to close form
  const handleSendUpdate = async () => {
    await sendUpdate();
    setShowComposer(false); // Close form after sending
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header with Toggle Button */}
      <div className="bg-white rounded-2xl shadow-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Bell className="h-5 w-5 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Event Updates</h2>
            </div>
            <p className="text-gray-600">
              Stay informed with the latest announcements and changes
            </p>
          </div>

          {/* NEW: Toggle Composer Button */}
          {canPostUpdate && (
            <button
              onClick={() => setShowComposer(!showComposer)}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center gap-2 shadow-sm"
            >
              {showComposer ? (
                <>
                  <X className="h-5 w-5" />
                  Cancel
                </>
              ) : (
                <>
                  <Bell className="h-5 w-5" />
                  Post Update
                </>
              )}
            </button>
          )}
        </div>

        {/* Update Composer - CONDITIONALLY RENDERED */}
        {canPostUpdate && showComposer && (
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-6 border border-emerald-200 animate-in slide-in-from-top duration-300">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1 space-y-4">
                <textarea
                  value={updateText}
                  onChange={(e) => setUpdateText(e.target.value)}
                  placeholder="Share an important update about your event..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  {eventSettings.moderateUpdates && (
                    <p className="text-sm text-emerald-700 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-orange-500" />
                      Updates are reviewed before publishing
                    </p>
                  )}
                  <div className="flex items-center gap-3 ml-auto">
                    <button
                      onClick={() => {
                        setShowComposer(false);
                        setUpdateText('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendUpdate}
                      disabled={!updateText.trim() || sendingUpdate}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {sendingUpdate ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <Bell className="h-4 w-4" />
                      )}
                      {sendingUpdate ? 'Publishing...' : 'Publish Update'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Updates List */}
      <div className="space-y-4">
        {publicUpdates.length > 0 ? (
          publicUpdates.map((update) => (
            <UpdateCard 
              key={update.id} 
              update={update}
              bookId={bookId}
              eventSettings={eventSettings}
              onReload={onReload}
            />
          ))
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No updates yet</h3>
            <p className="text-gray-600">
              {canPostUpdate ? 
                'Be the first to share an update!' : 
                'Updates will appear here when organizers share them.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// NEW: Message Card Component
const MessageCard = ({ message, bookId, eventSettings, onReload }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(message.likes || 0);

  const handleLike = async () => {
    try {
      const newLiked = !liked;
      setLiked(newLiked);
      setLikeCount(prev => newLiked ? prev + 1 : prev - 1);
      
      // Now message.id is available because we're inside MessageCard
      await eventsService.toggleMessageLike(bookId, message.id, newLiked);
    } catch (error) {
      // Revert on error
      setLiked(!liked);
      setLikeCount(prev => liked ? prev + 1 : prev - 1);
      console.error('Error toggling like:', error);
    }
  };

  const isPending = message.status === 'pending';

  return (
    <div className={`bg-white rounded-2xl shadow-sm p-4 ${isPending ? 'opacity-75 border-2 border-orange-200' : ''}`}>
      {isPending && (
        <div className="flex items-center gap-2 mb-4 text-orange-600 text-sm">
          <Clock className="h-4 w-4" />
          Pending moderation review
        </div>
      )}
      
      <div className="flex items-start gap-2">
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
          {message.authorAvatar ? (
            <img src={message.authorAvatar} alt={message.authorName} className="w-full h-full object-cover" />
          ) : (
            <User className="h-5 w-5 text-gray-400" />
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-medium text-gray-900">
              {message.authorName || 'Anonymous'}
            </h4>
            {message.isOrganizer && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">
                <Shield className="h-3 w-3 mr-1" />
                Organizer
              </span>
            )}
            <span className="text-sm text-gray-500">
              {new Date(message.createdAt).toLocaleDateString()}
            </span>
          </div>
          
          <p className="text-gray-700 mb-4 leading-relaxed">
            {message.content}
          </p>
          
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${
                liked 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <ThumbsUp className="h-4 w-4" />
              <span className="text-sm">{likeCount}</span>
            </button>
            
            <button className="flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Flag className="h-4 w-4" />
              <span className="text-sm">Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// NEW: Update Card Component
const UpdateCard = ({ update, isLatest, onReload }) => {
  return (
    <div className={`bg-white rounded-2xl shadow-sm p-6 ${isLatest ? 'ring-2 ring-emerald-200' : ''}`}>
      {isLatest && (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700 mb-4">
          <Star className="h-3 w-3" />
          Latest Update
        </div>
      )}
      
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl flex items-center justify-center">
          <Bell className="h-6 w-6 text-white" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="font-semibold text-gray-900">Event Update</h3>
            <span className="text-sm text-gray-500">
              {new Date(update.createdAt).toLocaleDateString()} at{' '}
              {new Date(update.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed">
              {update.content}
            </p>
          </div>
          
          {update.authorName && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <User className="h-4 w-4" />
              Posted by {update.authorName}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const difference = +targetDate - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    }

    return timeLeft;
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearTimeout(timer);
  });

  const timerComponents = [];

  Object.keys(timeLeft).forEach((interval) => {
    if (!timeLeft[interval]) {
      return;
    }

    timerComponents.push(
      <div key={interval} className="text-center">
        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-3">
          <div className="text-3xl font-bold">{timeLeft[interval]}</div>
          <div className="text-sm opacity-75">{interval}</div>
        </div>
      </div>
    );
  });

  return (
    <div>
      <p className="text-lg mb-4 opacity-90">Event starts in:</p>
      <div className="flex justify-center gap-4">
        {timerComponents.length ? timerComponents : <span>Event has started!</span>}
      </div>
    </div>
  );
};

// Overview Section Component
const EventOverview = ({ bookData, eventSettings, contactInfo, analytics, organizers, messages, onViewAllMessages }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <div className="lg:col-span-2 space-y-8">
      {/* About Section */}
      <div className="bg-white rounded-xl shadow-sm p-8">
        <h2 className="text-2xl font-bold mb-4">About This Event</h2>
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-700 leading-relaxed text-lg">
            {bookData.description || 'Event description will appear here.'}
          </p>
          {bookData.specialInstructions && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
              <h4 className="font-medium text-yellow-800 mb-2">Special Instructions</h4>
              <p className="text-sm text-yellow-700">{bookData.specialInstructions}</p>
            </div>
          )}
        </div>
      </div>

      {/* Messages Preview Section - NEW */}
      {eventSettings.enableMessages && (
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Messages</h2>
                <p className="text-sm text-gray-600">
                  {messages?.filter(m => m.isPublic === true).length || 0} messages
                </p>
              </div>
            </div>
            {messages?.filter(m => m.isPublic === true).length > 0 && (
              <button
                onClick={onViewAllMessages}
                className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-2"
              >
                View All
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>

          {messages?.length > 0 ? (
            <div className="space-y-4">
              {messages.slice(0, 3).map((message, index) => (
                <div key={message.id || index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-medium text-gray-900">
                        {message.userName || message.authorName || 'Anonymous'}
                      </p>
                      <span className="text-sm text-gray-500">
                        {new Date(message.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm line-clamp-2">
                      {message.content || message.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500">
                {eventSettings.messagingRoles?.includes('anyone') ? 
                  'Be the first to start the conversation!' : 
                  'Messages will appear here when attendees start sharing.'
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>

    <div className="space-y-6">
      {/* Event Info Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-bold mb-4">Event Information</h3>
        <div className="space-y-3">
          {eventSettings.eventDate && (
            <div className="flex justify-between">
              <span className="text-gray-600">Date & Time</span>
              <span className="font-medium">{new Date(eventSettings.eventDate).toLocaleDateString()}</span>
            </div>
          )}
          {eventSettings.eventLocation && (
            <div className="flex justify-between">
              <span className="text-gray-600">Location</span>
              <span className="font-medium">{eventSettings.eventLocation}</span>
            </div>
          )}
          {eventSettings.maxAttendees && (
            <div className="flex justify-between">
              <span className="text-gray-600">Capacity</span>
              <span className="font-medium">{eventSettings.maxAttendees} people</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Price</span>
            <span className="font-medium">
              {eventSettings.requirePayment ? `$${eventSettings.eventPrice || 0}` : 'Free'}
            </span>
          </div>
        </div>
      </div>

      {/* Primary Organizer Card */}
      {organizers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-bold mb-4">
            {organizers.length === 1 ? 'Organizer' : 'Organizers'}
          </h3>
          <div className="space-y-4">
            {organizers.slice(0, 2).map((organizer) => (
              <div key={organizer.id} className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
                  {organizer.avatar ? (
                    <img 
                      src={organizer.avatar} 
                      alt={organizer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{organizer.name}</p>
                  <p className="text-sm text-gray-600 truncate">{organizer.role || 'Organizer'}</p>
                </div>
              </div>
            ))}
          </div>
          {organizers.length > 2 && (
            <button
              onClick={() => onViewAllMessages && onViewAllMessages('organizers')}
              className="w-full mt-4 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              View all {organizers.length} organizers
            </button>
          )}
        </div>
      )}
    </div>
  </div>
);

// Event Details Component
const EventDetails = ({ bookData, eventSettings }) => (
  <div className="max-w-4xl mx-auto">
    <div className="bg-white rounded-xl shadow-sm p-8">
      <h2 className="text-2xl font-bold mb-6">Event Details</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-900">When & Where</h3>
          <div className="space-y-4">
            {eventSettings.eventDate && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-emerald-600 mt-1" />
                <div>
                  <p className="font-medium">{new Date(eventSettings.eventDate).toLocaleDateString()}</p>
                  <p className="text-sm text-gray-600">{new Date(eventSettings.eventDate).toLocaleTimeString()}</p>
                </div>
              </div>
            )}
            
            {eventSettings.eventLocation && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-emerald-600 mt-1" />
                <div>
                  <p className="font-medium">{eventSettings.eventLocation}</p>
                  {eventSettings.venue && (
                    <p className="text-sm text-gray-600">{eventSettings.venue}</p>
                  )}
                  {eventSettings.venueDetails && (
                    <p className="text-sm text-gray-600 mt-1">{eventSettings.venueDetails}</p>
                  )}
                </div>
              </div>
            )}

            {eventSettings.duration && (
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-emerald-600 mt-1" />
                <div>
                  <p className="font-medium">Duration: {eventSettings.duration}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Additional Information</h3>
          <div className="space-y-4">
            {eventSettings.parkingInfo && (
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Parking</h4>
                <p className="text-sm text-gray-600">{eventSettings.parkingInfo}</p>
              </div>
            )}
            
            {eventSettings.publicTransport && (
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Public Transportation</h4>
                <p className="text-sm text-gray-600">{eventSettings.publicTransport}</p>
              </div>
            )}
            
            {eventSettings.accessibilityInfo && (
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Accessibility</h4>
                <p className="text-sm text-gray-600">{eventSettings.accessibilityInfo}</p>
              </div>
            )}

            {eventSettings.mapLink && (
              <div>
                <a
                  href={eventSettings.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  <MapPin className="h-4 w-4" />
                  View on Map
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Event Booking Section Component
const EventBookingSection = ({ bookData, eventSettings, onOpenBooking }) => (
  <div className="max-w-2xl mx-auto">
    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CreditCard className="h-8 w-8 text-emerald-600" />
      </div>
      
      <h2 className="text-2xl font-bold mb-4">Reserve Your Spot</h2>
      <p className="text-gray-600 mb-8">
        {eventSettings.requirePayment ? 
          `Secure your place at this event for $${eventSettings.eventPrice || 0} per person.` :
          'This is a free event. Reserve your spot to receive updates and event details.'
        }
      </p>

      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-emerald-600">
              {eventSettings.requirePayment ? `$${eventSettings.eventPrice || 0}` : 'FREE'}
            </p>
            <p className="text-sm text-gray-600">per person</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">{eventSettings.maxAttendees || '∞'}</p>
            <p className="text-sm text-gray-600">max capacity</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-purple-600">
              {eventSettings.bookingDeadline ? 
                new Date(eventSettings.bookingDeadline).toLocaleDateString() : 
                'Open'
              }
            </p>
            <p className="text-sm text-gray-600">booking deadline</p>
          </div>
        </div>
      </div>

      <button
        onClick={onOpenBooking}
        className="w-full bg-emerald-600 text-white py-4 px-8 rounded-lg font-semibold hover:bg-emerald-700 transition-colors text-lg"
      >
        {eventSettings.requirePayment ? 'Book & Pay Now' : 'Reserve Your Spot'}
      </button>

      <p className="text-xs text-gray-500 mt-4">
        {eventSettings.requirePayment ? 
          'Secure payment processing. Refunds available up to 48 hours before the event.' :
          'No payment required. You can cancel anytime before the event.'
        }
      </p>
    </div>
  </div>
);